import React, { memo, useCallback, useMemo } from 'react';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import PropTypes from 'prop-types';

import usePopover from 'hooks/usePopover';

import { AppraisalOrderContactsCardTestIds } from '../../AppraisalOrderContactsCard.testIds';

import { orderContactActionIcons } from './OrderContactActions.constants';
import { getOrderContactActionItems } from './OrderContactActions.utils';

const OrderContactActions = ({
  rowId,
  rowKey,
  source,
  contact = null,
  isPrimary = false,
  isLocked = false,
  isOtherKind = false,
  isDisabled = false,
  onSetPrimary,
  onEditManualEntry,
  onOpenAgentAndSeller,
  onClearContact,
  onRemoveContact,
}) => {
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const onSetPrimaryClick = useCallback(() => onSetPrimary(rowId), [onSetPrimary, rowId]);
  const onEditClick = useCallback(() => onEditManualEntry(rowId), [onEditManualEntry, rowId]);
  const onClearClick = useCallback(() => onClearContact(rowId), [onClearContact, rowId]);
  const onRemoveClick = useCallback(() => onRemoveContact(rowId), [onRemoveContact, rowId]);

  const items = useMemo(
    () =>
      getOrderContactActionItems({
        rowKey,
        contact,
        source,
        isPrimary,
        isLocked,
        isOtherKind,
        icons: orderContactActionIcons,
        handlers: {
          onSetPrimary: onSetPrimaryClick,
          onEdit: onEditClick,
          onOpenAgentAndSeller,
          onClear: onClearClick,
          onRemove: onRemoveClick,
        },
      }),
    [
      rowKey,
      contact,
      source,
      isPrimary,
      isLocked,
      isOtherKind,
      onSetPrimaryClick,
      onEditClick,
      onOpenAgentAndSeller,
      onClearClick,
      onRemoveClick,
    ],
  );

  return (
    <>
      <MoreButtonCDS
        data-testid={AppraisalOrderContactsCardTestIds.getActionsButtonId(rowKey)}
        disabled={isDisabled}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />

      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
    </>
  );
};

OrderContactActions.propTypes = {
  rowId: PropTypes.string.isRequired,
  rowKey: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  onSetPrimary: PropTypes.func.isRequired,
  onEditManualEntry: PropTypes.func.isRequired,
  onOpenAgentAndSeller: PropTypes.func.isRequired,
  onClearContact: PropTypes.func.isRequired,
  onRemoveContact: PropTypes.func.isRequired,
  contact: PropTypes.shape({
    isManual: PropTypes.bool,
    isAgent: PropTypes.bool,
  }),
  isPrimary: PropTypes.bool,
  isLocked: PropTypes.bool,
  isOtherKind: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

export default memo(OrderContactActions);
